/**
 * <stz-music-play> — the preview play control (snippets/stz-music-play.liquid).
 *
 * A real <button> plays and pauses a hidden <audio preload="none">. The native player is never
 * shown: Chrome's native controls carry a Download item (Jade, 2026-10-20). controlslist and the
 * context-menu block are belt and braces on top of that.
 *
 * Starting any preview pauses every other audio element on the page.
 */
/** @param {number} seconds */
const clock = (seconds) => {
  const whole = Math.max(0, Math.ceil(seconds || 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

class StzMusicPlay extends HTMLElement {
  /** @type {HTMLAudioElement | null} */
  #audio = null;

  connectedCallback() {
    if (this.#audio) return;
    const audio = this.querySelector('audio');
    const button = this.querySelector('[data-play]');
    if (!audio || !button) return;
    this.#audio = audio;

    const fill = /** @type {HTMLElement | null} */ (this.querySelector('[data-fill]'));
    const time = this.querySelector('[data-time]');
    const title = this.dataset.title || '';

    audio.removeAttribute('controls');
    audio.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
    audio.addEventListener('contextmenu', (event) => event.preventDefault());

    /** @param {boolean} playing */
    const setPlaying = (playing) => {
      this.toggleAttribute('data-playing', playing);
      button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} preview of ${title}`);
    };
    const draw = () => {
      const total = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 30;
      if (fill) fill.style.transform = `scaleX(${Math.min(1, audio.currentTime / total)})`;
      if (time) time.textContent = clock(total - audio.currentTime);
    };

    button.addEventListener('click', () => {
      if (!audio.paused) {
        audio.pause();
        return;
      }
      audio.play().catch(() => setPlaying(false));
    });
    audio.addEventListener('play', () => {
      for (const other of document.querySelectorAll('audio')) {
        if (other !== audio) other.pause();
      }
      setPlaying(true);
    });
    audio.addEventListener('pause', () => setPlaying(false));
    audio.addEventListener('timeupdate', draw);
    audio.addEventListener('loadedmetadata', draw);
    audio.addEventListener('ended', () => {
      audio.currentTime = 0;
      setPlaying(false);
      draw();
    });
  }
}

if (!customElements.get('stz-music-play')) {
  customElements.define('stz-music-play', StzMusicPlay);
}
